<template>
  <tab-content
    :title="validateOnly ? 'Geometry Validation':'Geometry Settings'"
    :before-change="submit"
  >
    <template v-if="!validateOnly">
      <GeomColumn
        v-model="selectedCsvColumn"
        :v="$v.form.selectedCsvColumn"
        :options="columnOptions"
        :uploading="loading"
      />
      <GeomIDs
        v-model="selectedGeomID"
        :v="$v.form.selectedGeomID"
        :geom="geom"
        :options="geomIdOptions"
        :uploading="loading"
      >
        <template v-slot:warningmessage>
          <CsvGeomIDs
            :v="$v.form.selectedGeomID"
            :ignore="ignoreCsvError"
            :selected="selectedGeomID"
            :csv-column="selectedCsvColumn"
            @valid="updatevalid"
            @ignore="ignoreCsvError=true"
          />
        </template>
      </GeomIDs>

      <GeomConfig
        v-model="selectedName"
        :v="$v.form.selectedName"
        :geom="geom"
        :options="geomIdOptions"
        :uploading="loading"
      />
    </template>

    <template v-else>
      <p>Validating existing geometries against new CSV</p>
      <h1 v-if="!$v.$anyError && !ignoreCsvError">
        <font-awesome-icon
          class="faa-bounce animated"
          :style="{color: 'green'}"
          icon="check-circle"
          size="lg"
        />
      </h1>
      <CsvGeomIDs
        :v="$v.form.selectedGeomID"
        :ignore="ignoreCsvError"
        :selected="selectedGeomID"
        :csv-column="selectedCsvColumn"
        @valid="updatevalid"
        @ignore="ignoreCsvError=true"
        @show="submit"
      />
    </template>
  </tab-content>
</template>

<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'

import GeomColumn from '@/components/admin/upload/ShpSettings/GeomColumn.vue'
import CsvGeomIDs from '@/components/admin/upload/ShpSettings/CsvGeomIDs.vue'
import GeomIDs from '@/components/admin/upload/ShpSettings/GeomIDs.vue'
import GeomConfig from '@/components/admin/upload/ShpSettings/GeomConfig.vue'
import { required } from 'vuelidate/lib/validators'
import { postGeom } from '@/api'
import { Feature } from 'geojson'
import { APP_CONFIG_KEY, GEOM_CONFIG_KEY } from '@/constants'
import { useUploadStore } from '@/store/admin/upload'

const components = {
  GeomColumn,
  CsvGeomIDs,
  GeomIDs,
  GeomConfig,
}

const props = {
  loading: {
    type: Boolean,
    default: false,
  },
  validateOnly: Boolean,
}

// eslint-disable-next-line no-use-before-define
@Component<ShpSettings>({
  components,
  watch: {
    validateOnly () {
      this.$v.$touch()
    },
    selectedGeomID (value: string) {
      if (!this.selectedName || this.selectedName === 'none') {
        this.selectedName = value
      }
    },
  },
})
export default class ShpSettings extends Vue.extend({ props }) {
  selectedName = ''
  csvGeoIdsAreInShp = false
  unusedGeoIdsAreInShp = false
  ignoreCsvError = false
  upload = useUploadStore()

  validations () {
    return {
      form: {
        selectedCsvColumn: {
          required: () => this.validateOnly || required,
        },
        selectedGeomID: {
          required: () => this.validateOnly || required,
          uniqueIDs: () => this.validateOnly || this.validateUniqueIDs,
          csvGeomIds: () => this.ignoreCsvError || this.csvGeoIdsAreInShp,
          unusedGeomIds: () => this.ignoreCsvError || this.unusedGeoIdsAreInShp,
        },
        selectedName: {
          required: () => this.validateOnly || required,
        },
      },
    }
  }

  mounted () {
    this.selectedName = this.defaultGeomID
  }

  get form () {
    return {
      selectedCsvColumn: this.selectedCsvColumn,
      selectedGeomID: this.selectedGeomID,
      selectedName: this.selectedName,
    }
  }

  get geom () {
    const geoms = this.upload.geom
    if ('features' in geoms) {
      return geoms
    } else {
      return { features: [] }
    }
  }

  get geomIdOptions () {
    if (this.geom.features.length > 0) {
      const props = this.geom.features[0].properties
      return props ? Object.keys(props) : ['none']
    } else {
      return ['none']
    }
  }

  get validateUniqueIDs () {
    const uniqueIDs = []
    for (const feature of this.geom.features) {
      const id = feature.properties?.[this.selectedGeomID]
      if (id) uniqueIDs.push(id)
    }
    const duplicates = this.findDuplicates(uniqueIDs)
    return Boolean(duplicates.length === 0)
  }

  get columnOptions () {
    return this.upload.headers
  }

  get config () {
    return this.$typedStore.primary.app.config
  }

  get defaultGeomID () {
    return this.geomIdOptions[0]
  }

  get selectedGeomID () {
    return this.config?.appConfig?.geomShpProp ?? this.defaultGeomID
  }

  set selectedGeomID (newValue: string) {
    this.$typedStore.primary.app.updateAppConfig({ prop: 'geomShpProp', value: newValue })
  }

  get defaultCsvColumn () {
    if (this.columnOptions.length > 0) {
      return this.columnOptions[0]
    }
    return ''
  }

  get selectedCsvColumn (): string {
    return this.config?.appConfig?.geomColumn ?? this.defaultCsvColumn
  }

  set selectedCsvColumn (newValue: string) {
    this.$typedStore.primary.app.updateAppConfig({ prop: 'geomColumn', value: newValue })
  }

  async submit () {
    this.$v.$touch()
    if (!this.$v.$anyError) {
      if (this.validateOnly) {
        return true
      }
      const geomConfig = this.buildGeomConfig()
      this.$typedStore.primary.app.updateConfig({
        configPath: [GEOM_CONFIG_KEY],
        value: geomConfig,
      })

      await Promise.all([
        postGeom(this.upload.geom),
        this.$typedStore.primary.app.saveConfig(),
      ])
      return true
    } else {
      return false // validation failed
    }
  }

  updateAppConfig (prop: string, value: any) {
    this.$typedStore.primary.app.updateConfig({
      configPath: [APP_CONFIG_KEY, prop],
      value,
    })
  }

  findDuplicates (array: any[]) {
    return array.filter((item, index) => array.indexOf(item) !== index)
  }

  updatevalid (object: { key: 'csvGeoIdsAreInShp' | 'unusedGeoIdsAreInShp', value: boolean }) {
    this[object.key] = object.value
  }

  buildGeomConfig () {
    return (this.geom.features as Feature[]).reduce((alias: { [key: string]: string }, feature: Feature) => {
      if (feature.properties) alias[feature.properties[this.selectedGeomID]] = feature.properties[this.selectedName]
      return alias
    }, {})
  }
}

</script>
